import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { STARTER_QUESTIONS } from './knowledgeBase';
import { useChatBot } from './useChatBot';
import './chatWidget.css';

function AgentAvatar({ className = '' }) {
  return (
    <span className={`chat-avatar ${className}`.trim()} aria-hidden="true">
      <i className="bi bi-robot" />
    </span>
  );
}

function SourceTag({ source }) {
  if (source === 'ai') {
    return (
      <span className="chat-msg-tag chat-msg-tag--ai">
        <i className="bi bi-stars" aria-hidden="true" /> AI-generated
      </span>
    );
  }
  if (source === 'prepared') {
    return (
      <span className="chat-msg-tag chat-msg-tag--curated">
        <i className="bi bi-patch-check-fill" aria-hidden="true" /> Prepared answer
      </span>
    );
  }
  return null;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const { messages, status, send, reset, fallbackEnabled } = useChatBot();

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const launcherRef = useRef(null);

  // Focus the input on open, restore focus to the launcher on close.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    launcherRef.current?.focus();
    return undefined;
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Keep the transcript pinned to the latest message.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const busy = status === 'thinking' || status === 'streaming';

  const submit = (e) => {
    e.preventDefault();
    if (!draft.trim() || busy) return;
    send(draft);
    setDraft('');
  };

  const askStarter = (question) => {
    if (busy) return;
    send(question);
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e);
    }
  };

  return (
    <div className="chat-widget">
      <button
        type="button"
        ref={launcherRef}
        className={`chat-launcher${open ? ' is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="chat-launcher-avatar">
          <AgentAvatar />
          <span className="chat-launcher-pulse" aria-hidden="true" />
        </span>
        <span className="chat-launcher-text">
          <span className="chat-launcher-title">Ask Cyrus about me</span>
          <span className="chat-launcher-sub">Ehsan&apos;s AI assistant</span>
        </span>
        <i className="bi bi-chevron-up chat-launcher-caret" aria-hidden="true" />
      </button>

      {open && <div className="chat-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}

      <div
        className={`chat-panel${open ? ' is-open' : ''}`}
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-label="Chat with Cyrus, Ehsan's AI assistant"
        hidden={!open}
      >
        <header className="chat-panel-head">
          <AgentAvatar className="chat-panel-avatar" />
          <span className="chat-panel-id">
            <strong>I am Cyrus, Ehsan&apos;s AI Assistant.</strong>
            <span>For the answers you cannot find, contact Ehsan</span>
          </span>
          <span className="chat-panel-actions">
            {messages.length > 0 && (
              <button type="button" className="chat-icon-btn" onClick={reset} aria-label="Start over">
                <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
              </button>
            )}
            <button type="button" className="chat-icon-btn" onClick={() => setOpen(false)} aria-label="Close chat">
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </span>
        </header>

        <div className="chat-scroll" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="chat-intro">
              <p>
                Hi, I&apos;m <strong>Cyrus</strong> — Ehsan Faghih&apos;s AI assistant. Ask me about his
                research, projects, skills, availability, or interests.
              </p>
              <div className="chat-starters">
                {STARTER_QUESTIONS.map((q) => (
                  <button key={q} type="button" className="chat-starter" onClick={() => askStarter(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`chat-msg chat-msg--${msg.role === 'user' ? 'user' : 'assistant'}`}
            >
              {msg.role !== 'user' && <AgentAvatar className="chat-msg-avatar" />}
              <div className="chat-msg-body">
                {msg.role !== 'user' && <SourceTag source={msg.source} />}
                <p>
                  {msg.text}
                  {msg.pending && !msg.text && <span className="chat-typing-inline" aria-label="Thinking"><span /><span /><span /></span>}
                  {msg.pending && msg.text && <span className="chat-caret" aria-hidden="true" />}
                </p>
              </div>
            </div>
          ))}

          {status === 'thinking' && messages[messages.length - 1]?.role === 'user' && (
            <div className="chat-msg chat-msg--assistant">
              <AgentAvatar className="chat-msg-avatar" />
              <div className="chat-msg-body">
                <span className="chat-typing" aria-label="Thinking">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          )}
        </div>

        <form className="chat-input-row" onSubmit={submit}>
          <textarea
            ref={inputRef}
            className="chat-input"
            rows={1}
            maxLength={500}
            placeholder="Ask a question…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onInputKeyDown}
            disabled={busy}
          />
          <button type="submit" className="chat-send" disabled={!draft.trim() || busy} aria-label="Send">
            <i className="bi bi-send-fill" aria-hidden="true" />
          </button>
        </form>

        <p className="chat-disclaimer">
          {fallbackEnabled
            ? "I'm an agent, so I can be inaccurate — but I try to give you the answers you're looking for to help you get to know Ehsan a bit. More info -> email him!"
            : 'Cyrus is not connected yet. Answers come from a few prepared topics; for anything else, email Ehsan.'}
        </p>
      </div>
    </div>
  );
}
